import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { IconBuilding, IconLock, IconLoader, IconMail } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";
import { useMsg } from "../hooks/useMsg";

interface Profile {
  id: string;
  name: string;
  legal_name: string | null;
  company_id: string | null;
  vat_id: string | null;
  email: string | null;
  phone: string | null;
  locale: string;
  billing_address: string | null;
  billing_city: string | null;
  billing_postal_code: string | null;
  billing_country: string;
  email_verified_at: string | null;
  registered_at: string | null;
  last_activity_at: string | null;
}

export default function CompanyProfile() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [form, setForm] = useState<Partial<Profile>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const setMsg = useMsg();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<Profile>("/tenant/profile");
      setProfile(data);
      setForm({
        name: data.name, legal_name: data.legal_name ?? "", email: data.email ?? "",
        phone: data.phone ?? "", locale: data.locale,
        billing_address: data.billing_address ?? "", billing_city: data.billing_city ?? "",
        billing_postal_code: data.billing_postal_code ?? "", billing_country: data.billing_country,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await api.put<Profile>("/tenant/profile", form);
      setProfile(data);
      setMsg({ type: "ok", text: t("companyProfile.saved") });
    } catch (e: any) {
      setMsg({ type: "err", text: e?.response?.data?.detail || t("companyProfile.saveError") });
    } finally {
      setSaving(false);
    }
  }

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });
  const setValue = (k: string) => (val: string) =>
    setForm({ ...form, [k]: val });

  if (loading || !profile) {
    return <div className="card" style={{ textAlign: "center", padding: 48 }}><IconLoader size={32} className="muted" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("companyProfile.title")}</h1>
        <p>{t("companyProfile.subtitle")}</p>
      </div>

      {/* Locked fields banner */}
      <div className="card" style={{
        marginBottom: 24, display: "flex", gap: 16, alignItems: "center",
        background: "var(--yellow-dim)", borderColor: "transparent",
      }}>
        <IconLock size={24} style={{ color: "var(--yellow)", flexShrink: 0 }} />
        <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6, margin: 0 }}>
          <strong style={{ color: "var(--yellow)" }}>{t("companyProfile.lockedBanner")}</strong> {t("companyProfile.lockedBannerBody")}{" "}
          <a href="mailto:support@luigipos.cz">support@luigipos.cz</a>.
        </p>
      </div>

      <form onSubmit={save} className="card" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Company identity */}
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <IconBuilding size={18} className="muted" /> {t("companyProfile.companySection")}
          </h3>
          <div className="grid grid-2">
            <div>
              <label>IČO <span style={{ fontSize: 10, color: "var(--yellow)", border: "1px solid var(--yellow)", padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>{t("companyProfile.locked")}</span></label>
              <input value={profile.company_id ?? ""} disabled />
            </div>
            <div>
              <label>DIČ <span style={{ fontSize: 10, color: "var(--yellow)", border: "1px solid var(--yellow)", padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>{t("companyProfile.locked")}</span></label>
              <input value={profile.vat_id ?? ""} disabled />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label>{t("companyProfile.companyName")}</label>
              <input value={form.name ?? ""} onChange={set("name")} required />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label>{t("companyProfile.legalName")}</label>
              <input value={form.legal_name ?? ""} onChange={set("legal_name")} />
            </div>
          </div>
        </div>

        <hr />

        {/* Contact */}
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <IconMail size={18} className="muted" /> {t("companyProfile.contactSection")}
          </h3>
          <div className="grid grid-2">
            <div>
              <label>E-mail</label>
              <input value={form.email ?? ""} onChange={set("email")} />
            </div>
            <div>
              <label>{t("suppliers.phone")}</label>
              <input value={form.phone ?? ""} onChange={set("phone")} />
            </div>
            <div>
              <label>{t("companyProfile.language")}</label>
              <SearchableSelect
                value={form.locale ?? "cs"}
                onChange={setValue("locale")}
                options={[
                  { value: "cs", label: "Čeština" },
                  { value: "en", label: "English" },
                  { value: "sk", label: "Slovenčina" },
                ]}
                searchPlaceholder={t("companyProfile.searchLanguage")}
              />
            </div>
          </div>
        </div>

        <hr />

        {/* Billing address */}
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
            <IconBuilding size={18} className="muted" /> {t("companyProfile.billingAddress")}
          </h3>
          <div className="grid grid-2">
            <div style={{ gridColumn: "1 / -1" }}>
              <label>{t("companyProfile.street")}</label>
              <input value={form.billing_address ?? ""} onChange={set("billing_address")} />
            </div>
            <div>
              <label>{t("companyProfile.city")}</label>
              <input value={form.billing_city ?? ""} onChange={set("billing_city")} />
            </div>
            <div>
              <label>{t("companyProfile.postalCode")}</label>
              <input value={form.billing_postal_code ?? ""} onChange={set("billing_postal_code")} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label>{t("companyProfile.country")}</label>
              <input value={form.billing_country ?? ""} onChange={set("billing_country")} />
            </div>
          </div>
        </div>

        {/* Save */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
          <button type="submit" className="primary" disabled={saving}>
            {saving ? <IconLoader size={18} /> : t("customers.saveChanges")}
          </button>

        </div>
      </form>
    </div>
  );
}
